/**
 * readAloud/player — one readout at a time, across the whole app.
 *
 * A module singleton with a `useSyncExternalStore` subscription, the same idiom
 * the delivery ledger uses (services/messageDelivery): the transcript renders
 * hundreds of message rows and each one only needs to know "am I the one being
 * read?", so a single store beats per-row state and makes "one readout at a
 * time" true by construction rather than by convention.
 *
 * WHAT MAKES IT FEEL FAST. The server splits a message into sentence-bounded
 * segments and renders one per call, with a SHORT opening segment so the first
 * sound arrives in about a second. This player plays segment 0 the moment it
 * arrives, fetches segment 1 while 0 is playing AND assigns that URL to the
 * idle element the instant it lands — so the next segment's BYTES are already
 * downloading while the current one sounds, and the seam between them is a
 * `play()` on a buffered element rather than a cold ~1 MB download. (Until
 * 2026-09-10 this file's header claimed that buffering while `el.src` was
 * assigned only after the previous segment ended; the claim is now the code.)
 *
 * THE READER IS IN CHARGE (2026-09-10, Miles): while a readout is going the row
 * carries a player — previous / next section, pause and play, restart, and a
 * speed slider that moves in 10-percentage-point steps from 50 % to 200 %,
 * remembered for next time. Every one of those is a command on the run below:
 * a skip re-uses this run's URL cache, so moving back and forth never re-renders
 * audio and never costs a second cent.
 *
 * THE iOS UNLOCK IS LOAD-BEARING. Mobile Safari only lets an Audio element play
 * if it was played during a user gesture — and the first fetch happens after the
 * click, which breaks the gesture chain. So both elements are primed with a
 * silent clip synchronously inside the click, before anything is awaited. Take
 * that away and the control works everywhere except a phone, which is where it
 * matters most.
 *
 * Deliberately NOT stopped when the tab is hidden: somebody having a message
 * read to them switches tabs and keeps listening. It stops on a second click,
 * on a thread switch, and when the surface unmounts.
 */

import { fetchReadAloudSegment, type ReadAloudSegment } from './client';
import { cancelDeviceVoice, deviceVoiceAvailable, speakWithDeviceVoice } from './deviceVoice';
import { readAloudPlaybackFailedNotice } from './readAloudCopy';

export type ReadAloudState = 'idle' | 'loading' | 'playing' | 'paused';

export interface ReadAloudSnapshot {
  /** The message being read, as `${threadId}|${messageId}`, or null. */
  key: string | null;
  state: ReadAloudState;
  /** Which segment is sounding, and how many there are (for the label). */
  segment: number;
  segmentCount: number;
  /** Playback speed, 0.5–2.0 in 0.1 steps — the viewer's, remembered. */
  rate: number;
  /** D9 copy for a readout that did not happen, or that dropped out. */
  notice: string | null;
  /** The message the notice belongs to, so only that row shows it. */
  noticeKey: string | null;
}

export interface ReadAloudTarget {
  threadId: string;
  messageId: string;
  /** What the reader is looking at: the device-voice text, and the store-miss fallback. */
  text: string;
  language?: string;
}

/** Speed bounds, in the reader's own terms: 50 % … 200 %, ten points at a time. */
export const READ_ALOUD_MIN_RATE = 0.5;
export const READ_ALOUD_MAX_RATE = 2;
export const READ_ALOUD_RATE_STEP = 0.1;
export const READ_ALOUD_RATE_KEY = 'prest0n:readAloud:rate';

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  const snapped = Math.round(rate * 10) / 10;
  return Math.min(READ_ALOUD_MAX_RATE, Math.max(READ_ALOUD_MIN_RATE, snapped));
}

function storedRate(): number {
  try {
    const raw = window.localStorage.getItem(READ_ALOUD_RATE_KEY);
    if (!raw) return 1;
    return clampRate(Number(raw));
  } catch {
    return 1;
  }
}

const IDLE: ReadAloudSnapshot = {
  key: null,
  state: 'idle',
  segment: 0,
  segmentCount: 0,
  rate: 1,
  notice: null,
  noticeKey: null,
};

/** A 0-sample WAV. Primes an Audio element inside a gesture without a sound. */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAAB3AQACABAAZGF0YQAAAAA=';

let snapshot: ReadAloudSnapshot = { ...IDLE, rate: storedRate() };
const listeners = new Set<() => void>();

/** Bumped on every start and every stop; async work from an older run is dropped. */
let generation = 0;

let elements: HTMLAudioElement[] = [];
let unlocked = false;

/** What ended a segment's playback — 'seek' and 'stopped' are the reader's own doing. */
type PlayOutcome = 'ended' | 'failed' | 'seek' | 'stopped';

interface ActiveRun {
  run: number;
  key: string;
  target: ReadAloudTarget;
  total: number;
  /** The segment the reader is on. A skip writes it; the loop reads it every turn. */
  index: number;
  /** This run's segments by index — a skip back plays from here, never a re-render. */
  cache: Map<number, ReadAloudSegment>;
  /** The element carrying the segment that is sounding right now. */
  el: HTMLAudioElement | null;
  /** Ends the sounding segment with the given outcome (a skip, a stop). */
  interrupt: ((how: PlayOutcome) => void) | null;
  /** Segments whose transport already failed once — the second failure is real. */
  retried: Set<number>;
}

let active: ActiveRun | null = null;

function publish(next: Partial<ReadAloudSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a bad subscriber must not stop playback */
    }
  });
}

export function subscribeReadAloud(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readAloudSnapshot(): ReadAloudSnapshot {
  return snapshot;
}

export function readAloudKey(threadId: string | null | undefined, messageId: string): string {
  return `${threadId || ''}|${messageId}`;
}

function audioElements(): HTMLAudioElement[] {
  if (typeof document === 'undefined') return [];
  if (elements.length === 0) {
    elements = [new Audio(), new Audio()];
    for (const el of elements) {
      el.preload = 'auto';
      // Keeps iOS from taking the readout fullscreen or over the video surface.
      el.setAttribute('playsinline', 'true');
    }
  }
  return elements;
}

/**
 * Prime both elements inside the click. Called synchronously from `toggle`,
 * before any await — that is the whole point.
 */
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  for (const el of audioElements()) {
    try {
      el.src = SILENT_WAV;
      const p = el.play();
      if (p && typeof p.then === 'function') p.then(() => el.pause()).catch(() => undefined);
    } catch {
      /* an element that refuses to prime still tries to play later */
    }
  }
}

function halt(): void {
  cancelDeviceVoice();
  for (const el of elements) {
    try {
      el.pause();
      // Drop the source too: a paused element keeps downloading the rest.
      el.removeAttribute('src');
      el.load();
    } catch {
      /* already gone */
    }
  }
}

export function stopReadAloud(): void {
  generation += 1;
  const run = active;
  active = null;
  run?.interrupt?.('stopped');
  halt();
  publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
}

/**
 * A thread switch ends a readout that belonged to the thread being left.
 *
 * A missing id stops NOTHING: the host calls this with the previous thread,
 * which is null on the first render, and a readout must not be cancelled by the
 * surface simply mounting.
 */
export function stopReadAloudForThread(threadId: string | null | undefined): void {
  const id = String(threadId || '').trim();
  if (!id || !snapshot.key) return;
  if (snapshot.key.startsWith(`${id}|`)) stopReadAloud();
}

export function dismissReadAloudNotice(): void {
  if (snapshot.notice === null && snapshot.noticeKey === null) return;
  publish({ notice: null, noticeKey: null });
}

// ── The reader's controls (2026-09-10) ──────────────────────────────────────

/** Hold the readout where it is. The bytes stay buffered; play() resumes it. */
export function pauseReadAloud(): void {
  if (!active || snapshot.state !== 'playing' || !active.el) return;
  try {
    active.el.pause();
  } catch {
    /* an element that will not pause is already gone */
  }
  publish({ state: 'paused' });
}

/** Carry on from exactly where the pause landed. */
export function resumeReadAloud(): void {
  if (!active || snapshot.state !== 'paused' || !active.el) return;
  publish({ state: 'playing' });
  try {
    const p = active.el.play();
    if (p && typeof p.catch === 'function') p.catch(() => undefined);
  } catch {
    /* the loop's own error handler owns the failure path */
  }
}

/** One control for the reader: play what is paused, pause what is playing. */
export function toggleReadAloudPause(): void {
  if (snapshot.state === 'paused') resumeReadAloud();
  else pauseReadAloud();
}

/** Jump to a section by index — the loop plays it from this run's cache. */
function goToSegment(index: number): void {
  if (!active) return;
  const target = Math.min(Math.max(0, index), Math.max(0, active.total - 1));
  active.index = target;
  // A skip is a fresh chance for a segment whose transport failed once.
  active.retried.delete(target);
  publish({ segment: target, state: 'playing' });
  active.interrupt?.('seek');
}

/** Move between the message's spoken sections: -1 back, +1 forward. */
export function seekReadAloudSegment(delta: number): void {
  if (!active) return;
  goToSegment(active.index + delta);
}

/** Back to the first section, from the top. */
export function restartReadAloud(): void {
  if (!active) return;
  goToSegment(0);
}

/**
 * Speed, in the reader's own units: 50 % … 200 %, snapped to ten points. The
 * choice is remembered for the next readout (per browser, per device) and is
 * applied to the sounding element at once, so the slider moves the voice under
 * the finger rather than at the next section.
 */
export function setReadAloudRate(rate: number): void {
  const next = clampRate(rate);
  if (next === snapshot.rate) return;
  for (const el of elements) {
    try {
      el.playbackRate = next;
    } catch {
      /* an element that refuses keeps the last rate */
    }
  }
  try {
    window.localStorage.setItem(READ_ALOUD_RATE_KEY, String(next));
  } catch {
    /* a viewer with no storage still gets the rate for this run */
  }
  publish({ rate: next });
}

/** The − / + buttons and the arrow keys: one ten-point step at a time. */
export function stepReadAloudRate(steps: number): void {
  setReadAloudRate(snapshot.rate + steps * READ_ALOUD_RATE_STEP);
}

/** Play one URL on the given element and resolve when it finishes, fails, or is interrupted. */
function playOn(state: ActiveRun, el: HTMLAudioElement, url: string): Promise<PlayOutcome> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (how: PlayOutcome) => {
      if (settled) return;
      settled = true;
      el.onended = null;
      el.onerror = null;
      if (state.el === el) state.el = null;
      state.interrupt = null;
      resolve(how);
    };
    el.onended = () => finish('ended');
    el.onerror = () => finish('failed');
    state.el = el;
    state.interrupt = (how: PlayOutcome) => {
      try {
        el.pause();
      } catch {
        /* pausing a dead element is not a failure */
      }
      finish(how);
    };
    try {
      // An element PRIMED with this url already holds its bytes — assigning the
      // same src again would throw the buffer away and re-download it.
      if (el.src !== url) {
        el.src = url;
      } else {
        try {
          el.currentTime = 0;
        } catch {
          /* a fresh element has no timeline yet; it starts at 0 anyway */
        }
      }
      try {
        el.playbackRate = snapshot.rate;
      } catch {
        /* an element that refuses the rate still plays at 1 */
      }
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => finish('failed'));
    } catch {
      finish('failed');
    }
  });
}

/**
 * A refusal the SERVER made on purpose — no permission, the daily ceiling, an
 * empty wallet, nothing to read. These are answers, so the reader is told; the
 * device voice is not wheeled out to paper over them.
 */
function isDeliberateRefusal(reason: string | undefined): boolean {
  return reason === 'no-permission'
    || reason === 'not-a-participant'
    || reason === 'ceiling'
    || reason === 'out-of-work-units'
    || reason === 'no-text'
    || reason === 'disabled';
}

/**
 * Start, or stop if this message is already being read.
 *
 * MUST be called from the click handler itself (it primes audio synchronously).
 */
export function toggleReadAloud(target: ReadAloudTarget): void {
  const key = readAloudKey(target.threadId, target.messageId);
  if (snapshot.key === key) {
    stopReadAloud();
    return;
  }
  unlock();
  generation += 1;
  const run = generation;
  active?.interrupt?.('stopped');
  active = null;
  halt();
  publish({ key, state: 'loading', segment: 0, segmentCount: 0, notice: null, noticeKey: null });
  void drive(run, key, target);
}

/** This run's copy of a segment: the cache first, the server only when it must. */
async function segmentFor(state: ActiveRun, index: number): Promise<ReadAloudSegment | null> {
  const cached = state.cache.get(index);
  if (cached) return cached;
  const response = await fetchReadAloudSegment({
    threadId: state.target.threadId,
    messageId: state.target.messageId,
    segment: index,
    fallbackText: state.target.text.slice(0, 2000),
    language: state.target.language,
  });
  if (state.run !== generation) return null;
  if (response.segmentCount) state.total = Math.max(state.total, response.segmentCount);
  if (!response.ok || !response.segment) return null;
  state.cache.set(index, response.segment);
  return response.segment;
}

/**
 * Fetch the NEXT segment while this one plays and hand its url to the idle
 * element straight away, so its bytes are downloading before the reader can
 * hear the seam. `load()` is what starts that download.
 */
function primeAhead(state: ActiveRun, index: number, el: HTMLAudioElement | undefined): void {
  if (!el || index >= state.total) return;
  void segmentFor(state, index).then((segment) => {
    if (!segment || state.run !== generation) return;
    if (state.el === el) return; // it started sounding on this element already
    try {
      if (el.src !== segment.url) {
        el.src = segment.url;
        el.load();
      }
    } catch {
      /* priming is an optimisation; the play path assigns it again if needed */
    }
  });
}

async function drive(run: number, key: string, target: ReadAloudTarget): Promise<void> {
  const els = audioElements();
  const state: ActiveRun = {
    run,
    key,
    target,
    total: 1,
    index: 0,
    cache: new Map(),
    el: null,
    interrupt: null,
    retried: new Set(),
  };
  active = state;

  const opening = await fetchReadAloudSegment({
    threadId: target.threadId,
    messageId: target.messageId,
    segment: 0,
    fallbackText: target.text.slice(0, 2000),
    language: target.language,
  });
  if (run !== generation) return;

  if (!opening.ok || !opening.segment) {
    active = null;
    if (isDeliberateRefusal(opening.reason)) {
      publish({ key: null, state: 'idle', notice: opening.notice || null, noticeKey: key });
      return;
    }
    // The voice could not be had. Rather than dead-end, read it here.
    if (deviceVoiceAvailable() && target.text.trim()) {
      publish({ state: 'playing', segmentCount: 1 });
      await speakWithDeviceVoice(target.text, target.language);
      if (run !== generation) return;
      publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
      return;
    }
    publish({ key: null, state: 'idle', notice: opening.notice || null, noticeKey: key });
    return;
  }

  state.total = Math.max(1, opening.segmentCount || 1);
  state.cache.set(0, opening.segment);
  publish({ state: 'playing', segment: 0, segmentCount: state.total });

  while (run === generation) {
    const index = state.index;
    const segment = await segmentFor(state, index);
    if (run !== generation) return;
    if (!segment) {
      if (!state.retried.has(index)) {
        state.retried.add(index);
        state.cache.delete(index);
        continue;
      }
      break;
    }

    const el = els[index % els.length];
    publish({ segment: index, segmentCount: state.total, state: snapshot.state === 'paused' ? 'playing' : snapshot.state });
    // Start the next fetch BEFORE waiting on this segment to finish playing, and
    // put its url on the other element — this overlap is what keeps a long
    // readout gapless.
    primeAhead(state, index + 1, els[(index + 1) % els.length]);

    const how = await playOn(state, el, segment.url);
    if (run !== generation) return;
    if (how === 'stopped') return;
    if (how === 'seek') continue;
    if (how === 'failed') {
      // A signed url can go stale and a socket can drop; ask for it once more
      // before telling the reader the voice is gone.
      if (!state.retried.has(index)) {
        state.retried.add(index);
        state.cache.delete(index);
        continue;
      }
      break;
    }
    if (index + 1 >= state.total) {
      active = null;
      publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
      return;
    }
    state.index = index + 1;
  }

  if (run !== generation) return;
  active = null;
  halt();
  publish({
    key: null,
    state: 'idle',
    segment: 0,
    segmentCount: 0,
    notice: readAloudPlaybackFailedNotice(target.language),
    noticeKey: key,
  });
}

/** Test seam: forget the elements and the unlock between cases. */
export function resetReadAloudPlayerForTests(): void {
  generation += 1;
  elements = [];
  unlocked = false;
  active = null;
  snapshot = { ...IDLE, rate: storedRate() };
  listeners.clear();
}
